import { useState, useCallback, useMemo, useTransition, useEffect } from "react";
import { EventFilterCondition, GamePageResponse, GPGamePlayerResponse } from "../../../types";
import { FilterCategory } from "../../../types";
import { transformArrayObjectToDropdownItems } from "../functions";
import { SelectValues } from "ui-kit/src/MultipleSelect/types";
import { useGameStore } from "../../../store";
import { shallowEqualArrays } from "../../../utils/shallowEqual";

interface UseFilterStateProps {
	gameData: GamePageResponse;
	defaultTeamId: number | null;
}

export const useFilterState = ({ gameData, defaultTeamId }: UseFilterStateProps) => {
	const { eventFilter } = useGameStore();
	const [periods, setPeriods] = useState<string[]>([]);
	const [filters, setFilters] = useState<EventFilterCondition[]>([]);
	const [team, setTeam] = useState<number | null>(defaultTeamId);
	const [selectedPlayer, setSelectedPlayer] = useState<SelectValues | null>(null);
	const [playerListByTeam, setPlayerListByTeam] = useState<any[]>([]);
	const [isPending, startTransition] = useTransition();

	const periodTabs = useMemo(() => {
		return (
			eventFilter?.getFilterViewsByCategories([FilterCategory.Period]).map((fw) => ({
				key: fw.value,
				label: fw.label,
			})) || []
		);
	}, [eventFilter]);

	const playerList = useMemo(() => {
		if (!gameData?.game || !team) return [];

		if (gameData?.homeGameTeam?.id === team) {
			return transformArrayObjectToDropdownItems(gameData.homeGameTeam.players, "id", "name", "numberSign");
		} else if (gameData?.awayGameTeam?.id === team) {
			return transformArrayObjectToDropdownItems(gameData.awayGameTeam.players, "id", "name", "numberSign");
		}
		return [];
	}, [gameData, team]);

	const memoizedFilters = useMemo<EventFilterCondition[]>(() => {
		const criteria: EventFilterCondition[] = [];
		if (team) {
			criteria.push({ type: "team", value: [team.toString()] });
		}
		if (selectedPlayer && selectedPlayer.length > 0) {
			const players = selectedPlayer.map((player) => player.value?.toString());
			criteria.push({ type: "player", value: players });
		}
		criteria.push({ type: "period", value: periods });
		return criteria;
	}, [team, selectedPlayer, periods]);

	const handlePeriodsChange = useCallback((keys: (string | number)[]) => {
		startTransition(() => {
			setPeriods(keys as string[]);
		});
	}, []);

	const handleTeamChange = useCallback((teamId: number) => {
		startTransition(() => {
			setTeam(teamId);
			setSelectedPlayer(null);
		});
	}, []);

	const handlePlayerChange = useCallback((player: SelectValues | null) => {
		startTransition(() => {
			setSelectedPlayer(player);
		});
	}, []);

	// Sync playerListByTeam and filters when dependencies change
	useEffect(() => {
		setPlayerListByTeam(playerList);
		setFilters(memoizedFilters);
	}, [playerList, memoizedFilters]);

	// Initialize periods from periodTabs
	useEffect(() => {
		if (periodTabs?.length > 0) {
			const keys = periodTabs.map((pt) => pt.key);
			// Use shallow comparison instead of JSON.stringify
			if (!shallowEqualArrays(periods, keys)) {
				startTransition(() => {
					setPeriods(keys);
				});
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [periodTabs]);

	return {
		periods,
		setPeriods,
		filters,
		setFilters,
		team,
		setTeam,
		selectedPlayer,
		setSelectedPlayer,
		handlePlayerChange,
		playerListByTeam,
		setPlayerListByTeam,
		periodTabs,
		playerList,
		memoizedFilters,
		handlePeriodsChange,
		handleTeamChange,
		isPending,
	};
};
